/**
 * #2017 — "Calls in group" on the call-sheet edit blocks (Individual + Bulk).
 *
 * The one rule (Ed, 2026-10-01): the quantity can NEVER go below the number the group had when
 * the edit was opened — "the amount coming in". ▲ adds calls with the group's position and times;
 * ▼ only takes back calls added in the same edit, before Save; existing calls leave only through
 * the per-call delete. The server enforces the same floor (CallController::callsToAdd), so these
 * rules are the display half, not the guard.
 *
 * Pure functions, the way `callListSearch.js` is, so both views agree and the rules can be
 * unit-tested without a DOM (`tests/calls-in-group-2017.test.mjs`).
 */

/** A whole number >= 0; anything unparseable is 0. */
function wholeOrZero(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
}

/**
 * The value the control may show: a whole number no lower than `floor`. A blank, NaN, negative
 * or fractional entry snaps to the floor / truncates, so a typed value below the opening count
 * is refused rather than carried.
 */
export function clampCallsInGroup(value, floor) {
  const f = wholeOrZero(floor);
  const n = Number(value);
  if (!Number.isFinite(n)) return f;
  return Math.max(f, Math.trunc(n));
}

/** How many calls Save will ADD — never negative. */
export function callsToAdd(wanted, floor) {
  const f = wholeOrZero(floor);
  return clampCallsInGroup(wanted, f) - f;
}

/** The identity of a call's group: same position (skill), start and end. */
export function groupKeyOf(call) {
  if (!call) return '';
  const skill = call.skill_id || (call.skill && call.skill.id) || '';
  return [String(skill), String(call.start || ''), String(call.end || '')].join('|');
}

/**
 * The size of the group `call` belongs to among the loaded rows — the Individual view's answer
 * to "calls in group". `rows` is the shape the page holds: an array of calls, or an object of
 * date => array of calls (job/{id}/callCalendar). Only the rows of the same job count when both
 * sides carry a job_id. The call itself is always counted once, so the answer is >= 1.
 */
export function groupSizeOf(rows, call) {
  if (!call) return 0;
  const key = groupKeyOf(call);
  const lists = Array.isArray(rows) ? [rows] : Object.values(rows || {});
  let n = 0;
  let sawSelf = false;
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const row of list) {
      if (!row) continue;
      if (call.job_id && row.job_id && String(call.job_id) !== String(row.job_id)) continue;
      if (groupKeyOf(row) !== key) continue;
      n++;
      if (call.id && row.id === call.id) sawSelf = true;
    }
  }
  return sawSelf || !call.id ? Math.max(n, 1) : n + 1;
}
